import { Instagram, Facebook, Phone, MapPin, MessageCircle, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACTS } from "@/data/contacts";
import { useLang } from "@/i18n";

// TikTok icon (not in lucide)
function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/>
    </svg>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  const { t } = useLang();
  return (
    <footer data-testid="footer" className="relative bg-smoke border-t border-white/5 pt-20 pb-10 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <Logo size={48} />
            <p className="mt-6 text-white/60 max-w-md leading-relaxed">
              {t("footer_intro")}
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              <SocialBtn href={CONTACTS.social.instagram} label="Instagram"><Instagram size={16} /></SocialBtn>
              <SocialBtn href={CONTACTS.social.facebook} label="Facebook"><Facebook size={16} /></SocialBtn>
              <SocialBtn href={CONTACTS.social.tiktok} label="TikTok"><TikTokIcon size={15} /></SocialBtn>
              <SocialBtn href={`https://wa.me/${CONTACTS.cellIntl}`} label="WhatsApp"><MessageCircle size={16} /></SocialBtn>
            </div>

            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold/80 mb-3">{t("footer_delivery")}</div>
              <div className="flex gap-2">
                <a href={CONTACTS.delivery.justeat} target="_blank" rel="noopener noreferrer" data-testid="footer-justeat"
                  className="inline-flex items-center gap-1.5 text-xs bg-[#FF8000]/15 hover:bg-[#FF8000]/25 border border-[#FF8000]/40 text-[#FFB366] px-3 py-1.5 rounded-full transition-colors">
                  Just Eat <ExternalLink size={10} />
                </a>
                <a href={CONTACTS.delivery.glovo} target="_blank" rel="noopener noreferrer" data-testid="footer-glovo"
                  className="inline-flex items-center gap-1.5 text-xs bg-[#00A082]/15 hover:bg-[#00A082]/25 border border-[#00A082]/40 text-[#33C8AA] px-3 py-1.5 rounded-full transition-colors">
                  Glovo <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold mb-5">{t("footer_hours")}</div>
            <ul className="space-y-2 text-white/70 text-sm">
              <li className="flex justify-between gap-4"><span>{t("footer_hours_all")}</span><span className="text-white">11:00 – 24:00</span></li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold mb-5">{t("footer_contacts")}</div>
            <ul className="space-y-3 text-white/70 text-sm">
              <li className="flex items-start gap-3"><MapPin size={15} className="mt-0.5 text-white/50" /> {CONTACTS.address}<br />{CONTACTS.city}</li>
              <li className="flex items-start gap-3">
                <Phone size={15} className="mt-0.5 text-white/50" />
                <a href={`tel:+39${CONTACTS.cell}`} className="hover:text-gold transition-colors" data-testid="footer-phone">Cell {CONTACTS.cellFormatted.replace('+39 ', '')}</a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle size={15} className="mt-0.5 text-white/50" />
                <a href={`tel:${CONTACTS.fixed}`} className="hover:text-gold transition-colors" data-testid="footer-tel-fisso">Tel/WA {CONTACTS.fixedFormatted.replace('+39 ', '')}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/40">
          <div>© {year} Ristorante Drago Ramen. {t("footer_rights")}</div>
          <div className="flex items-center gap-4 md:gap-6 flex-wrap justify-center">
            <span data-testid="footer-vat">P. IVA {CONTACTS.vat}</span>
            <span className="hidden md:inline">·</span>
            <a
              href="/admin/login"
              data-testid="footer-admin-link"
              className="inline-flex items-center gap-1.5 hover:text-gold transition-colors uppercase tracking-widest"
              aria-label="Area riservata"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Area Riservata
            </a>
            <span className="hidden md:inline">·</span>
            <span>Made with 龍 in Catania</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SocialBtn({ href, label, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`social-${label.toLowerCase()}`}
      className="w-10 h-10 rounded-full grid place-items-center bg-ash border border-white/10 text-white/70 hover:text-gold hover:border-gold transition-colors duration-300"
    >
      {children}
    </a>
  );
}
